export enum OptoDayPartTagType {
    Day = 'day',
    All = 'all',
    Night = 'night',
}

export const DEFAULT_FROM: Date | undefined = undefined;
export const DEFAULT_TO: Date | undefined = undefined;
export const DEFAULT_DAYPART_TAG = OptoDayPartTagType.Day;
export const ALL_DAYPART_TAGS: OptoDayPartTagType[] = [
    OptoDayPartTagType.All,
    OptoDayPartTagType.Day,
    OptoDayPartTagType.Night,
];
export const SMOLT_DAYPART_TAG = OptoDayPartTagType.All;
export const DEFAULT_WEIGHT_UNIT_TAG = 'g';

export const QUERY_DAYPART_TAG = 'd';
export const QUERY_WEIGHT_UNIT_TAG = 'w';
export const QUERY_SELECTED_FISH_GROUP_ID = 'fg';

const KNOWN_DAYPART_TAGS = new Set<string>(ALL_DAYPART_TAGS);

export function parseDaypartTag(value: unknown): OptoDayPartTagType | undefined {
    if (typeof value === 'string' && KNOWN_DAYPART_TAGS.has(value)) {
        return value as OptoDayPartTagType;
    }
    return undefined;
}

/**
 * Query params that describe *which cage data to look at*, and therefore stay
 * meaningful across every page of the same cage.
 *
 * Navigation has to carry these or the view silently resets: walking from the
 * dashboard to the graphs would drop the selected cohort, and the graphs would
 * answer a different question than the page you came from.
 *
 * **No date range is here** — neither `date-from` / `date-to` (the
 * `useDateRangePicker` params) nor the filter's own `from` / `to`, which the
 * lice and welfare pickers write. Carrying either made every pen page inherit
 * whatever range had been explored on the previous one — a dashboard is supposed
 * to open on the general window, not on the month someone zoomed into over in
 * the graphs. They were added to stop the cohort's window being lost on
 * navigation, but that job belongs to the cohort id: the period is resolved from
 * `fg` alone, so the window survives without pinning the literal dates.
 *
 * Consequence, and it is the intended one: a range picked on a page lives on
 * that page. Leave it, and you come back to the general view.
 */
export const CAGE_SCOPED_QUERY_PARAMS = [
    QUERY_DAYPART_TAG,
    QUERY_WEIGHT_UNIT_TAG,
    QUERY_SELECTED_FISH_GROUP_ID,
] as const;

/**
 * The cage-scoped params of a location search string, re-serialised.
 *
 * Returns `''` when none are present, so callers can append unconditionally.
 * Anything off the list is dropped: a param that belongs to one page (an open
 * modal, a picked range) must not follow the reader to the next one.
 *
 * Lives here rather than next to any one caller because both the sidebar and the
 * in-page links between pen pages need it, and two copies of a whitelist is how
 * one of them ends up missing the cohort id.
 */
export function cageScopedSearch(search: string): string {
    const current = new URLSearchParams(search);
    const carried = new URLSearchParams();

    for (const key of CAGE_SCOPED_QUERY_PARAMS) {
        const value = current.get(key);
        if (value != null && value !== '') {
            carried.set(key, value);
        }
    }

    const serialised = carried.toString();
    return serialised === '' ? '' : `?${serialised}`;
}

/**
 * A fish group id from the URL, or `undefined` when absent or unusable.
 *
 * Rejects anything that is not a positive integer rather than passing it on: the
 * id ends up as a query parameter the backend validates against the cage, and a
 * hand-edited URL should fall back to the cage's own data instead of provoking a
 * 400 on every request the page makes.
 */
export function parseFishGroupId(value: unknown): number | undefined {
    const raw = readQueryString(value);
    if (raw == null) return undefined;

    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * The selected cohort id from a location search string, or undefined.
 *
 * Exported because the breadcrumb needs the same answer from outside the cage
 * providers, and the rule that makes it safe — a positive integer only — has to
 * hold in both places.
 *
 * Reads the URL directly rather than the filter state, which is the point: the
 * breadcrumb renders above `CageFilterProvider` and cannot consume it.
 */
export function readSelectedFishGroupId(search: string): number | undefined {
    // A repeated `fg` names no group. The filter reads the URL through `parseQuery`, which
    // hands a repeated key back as an array that `parseFishGroupId` rejects; `get` would take
    // the first value instead, and the two would scope the same page to different subjects.
    const values = new URLSearchParams(search).getAll(QUERY_SELECTED_FISH_GROUP_ID);
    return values.length === 1 ? parseFishGroupId(values[0]) : undefined;
}

export function readQueryString(value: unknown): string | undefined {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}
